import type { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import type {
	LocalInterview,
	LocalInterviewParticipant,
	LocalInterviewPort,
	LocalInterviewSource,
} from '@Domain/Ports/LocalInterviews/LocalInterviewPort';
import SubjectAddress from '@Domain/Subjects/SubjectAddress';
import openDeviceDatabase, { LOCAL_INTERVIEWS_STORE_NAME } from '@Infra/Device/openDeviceDatabase';
import waitForIndexedDbRequest from '@Infra/Device/waitForIndexedDbRequest';
import waitForIndexedDbTransaction from '@Infra/Device/waitForIndexedDbTransaction';

/** Injected identifier source for newly minted interviews. */
type IdGenerator = {
	generate(): string;
};

/** Injected time source: lets `createdAt` be asserted exactly in tests instead of racing the wall clock. */
type Clock = {
	now(): number;
};

const OPERATION_ERROR_MESSAGE = "Échec de l'opération sur le magasin local d'entretiens.";
const TRANSACTION_ERROR_MESSAGE = "Échec de la transaction sur le magasin local d'entretiens.";
const TRANSACTION_ABORT_MESSAGE = 'Transaction sur le magasin local des entretiens interrompue.';
const INDEXED_DB_UNAVAILABLE_MESSAGE = "IndexedDB n'est pas disponible sur cet appareil.";
const INTERVIEW_NOT_FOUND_MESSAGE = 'Cet entretien local est introuvable.';

/**
 * A row as IndexedDB holds it: the structured clone drops class prototypes, so the subject is stored as
 * two plain fields. A row written before subjects were typed carries only `beneficiaryId`.
 */
type StoredLocalInterview = Omit<LocalInterview, 'subject'> & {
	readonly subjectTypeKey?: string;
	readonly subjectId?: number;
	readonly beneficiaryId?: number;
};

const FROZEN_EMPTY_SNAPSHOT: readonly LocalInterview[] = Object.freeze([]);

/**
 * Persists `LocalInterview` records in the `local-interviews` IndexedDB store, and holds the
 * owner-scoped in-memory cache that serves `snapshot()`. Unlike the transfer queue, this port
 * carries no processing, retry or backoff logic, so persistence and the observed cache live in a
 * single class rather than split across a store and a runner.
 *
 * The database is opened lazily, on the first call that genuinely needs it, and the connection is
 * memoised so building this store never deposits a database on the device by itself — building an
 * instance under jsdom, where `indexedDB` is absent, never throws.
 *
 * `start`/`stop` scope the in-memory cache to one `ownerUserId`, exactly as `OutboxPort` scopes its
 * own queue: `snapshot()` returns a frozen empty array before `start()`, and only ever reflects the
 * started owner's interviews afterwards. Writes (`create`, `attachOutboxItem`,
 * `attachServerIdentity`, `remove`) always reach storage regardless of session state — the transfer
 * handler that calls `get`/`attachServerIdentity` runs outside any started session's cache — but
 * only update the cache, and notify listeners, when the affected record belongs to the currently
 * started owner; a write for a different owner (or before any `start()`) is persisted but leaves
 * the observed snapshot untouched.
 */
class IndexedDbLocalInterviewStore implements LocalInterviewPort {
	private readonly indexedDbFactory: IDBFactory | undefined;
	private readonly idGenerator: IdGenerator;
	private readonly clock: Clock;
	private databasePromise: Promise<IDBDatabase> | null;
	private isStarted: boolean;
	private ownerUserId: number | null;
	private hydrated: boolean;
	private readonly interviewsCache: Map<string, LocalInterview>;
	private cachedSnapshotArray: readonly LocalInterview[];
	private readonly listeners: Set<(interviews: readonly LocalInterview[]) => void>;

	constructor(
		indexedDbFactory: IDBFactory | undefined = globalThis.indexedDB,
		idGenerator: IdGenerator = { generate: () => globalThis.crypto.randomUUID() },
		clock: Clock = { now: () => Date.now() },
	) {
		this.indexedDbFactory = indexedDbFactory;
		this.idGenerator = idGenerator;
		this.clock = clock;
		this.databasePromise = null;
		this.isStarted = false;
		this.ownerUserId = null;
		this.hydrated = false;
		this.interviewsCache = new Map();
		this.cachedSnapshotArray = FROZEN_EMPTY_SNAPSHOT;
		this.listeners = new Set();
	}

	start(options: { readonly ownerUserId: number }): void {
		if (this.isStarted) {
			this.stop();
		}

		this.isStarted = true;
		this.ownerUserId = options.ownerUserId;

		/**
		 * A hydration failure must never resolve into a "started" store sitting on an empty cache:
		 * `snapshot()` would then report an honest-looking empty list while interviews this device
		 * actually still holds go unrepresented. Falling back to `stop()` restores the pre-start
		 * reading instead — the frozen empty array that promises nothing — rather than one that
		 * wrongly asserts emptiness. The ownership recheck guards against a concurrent `stop()`
		 * followed by a fresh `start()` for a different owner racing this same async pass.
		 */
		void this.hydrate(options.ownerUserId).catch(() => {
			if (this.isStarted && this.ownerUserId === options.ownerUserId) {
				this.stop();
			}
		});
	}

	stop(): void {
		this.isStarted = false;
		this.ownerUserId = null;
		this.hydrated = false;
		this.interviewsCache.clear();
		this.cachedSnapshotArray = FROZEN_EMPTY_SNAPSHOT;
		this.notifyListeners();
	}

	snapshot(): readonly LocalInterview[] {
		if (!this.isStarted) {
			return FROZEN_EMPTY_SNAPSHOT;
		}

		return this.cachedSnapshotArray;
	}

	isHydrated(): boolean {
		return this.hydrated;
	}

	subscribe(listener: (interviews: readonly LocalInterview[]) => void): () => void {
		this.listeners.add(listener);

		return () => {
			this.listeners.delete(listener);
		};
	}

	async create(input: {
		readonly ownerUserId: number;
		readonly facilityId: number;
		readonly subject: SubjectAddress;
		readonly name?: string;
		readonly kind?: InterviewKind;
		readonly conductedOn: string;
		readonly participants: readonly LocalInterviewParticipant[];
		readonly consentGrantedAt: number | null;
		readonly participantsInformedAt?: number;
		readonly source: LocalInterviewSource;
		readonly preparationId?: number;
	}): Promise<LocalInterview> {
		const record: LocalInterview = {
			id: this.idGenerator.generate(),
			ownerUserId: input.ownerUserId,
			facilityId: input.facilityId,
			subject: input.subject,
			name: input.name,
			kind: input.kind,
			conductedOn: input.conductedOn,
			participants: input.participants,
			consentGrantedAt: input.consentGrantedAt,
			participantsInformedAt: input.participantsInformedAt,
			source: input.source,
			preparationId: input.preparationId,
			outboxItemId: null,
			interviewId: null,
			recordingId: null,
			createdAt: this.clock.now(),
		};

		await this.save(record);
		this.upsertCacheEntry(record);

		return record;
	}

	async get(id: string): Promise<LocalInterview | null> {
		const database = await this.openDatabase();
		const transaction = database.transaction(LOCAL_INTERVIEWS_STORE_NAME, 'readonly');
		const request = transaction.objectStore(LOCAL_INTERVIEWS_STORE_NAME).get(id);
		const record: StoredLocalInterview | undefined = await waitForIndexedDbRequest(
			request,
			OPERATION_ERROR_MESSAGE,
		);

		return record === undefined ? null : IndexedDbLocalInterviewStore.toLocalInterview(record);
	}

	async attachOutboxItem(id: string, outboxItemId: string): Promise<void> {
		const record = await this.getExistingOrThrow(id);
		const updatedRecord: LocalInterview = { ...record, outboxItemId };

		await this.save(updatedRecord);
		this.upsertCacheEntry(updatedRecord);
	}

	async attachServerIdentity(
		id: string,
		identity: { readonly interviewId: number; readonly recordingId: number },
	): Promise<void> {
		const record = await this.getExistingOrThrow(id);
		const updatedRecord: LocalInterview = {
			...record,
			interviewId: identity.interviewId,
			recordingId: identity.recordingId,
		};

		await this.save(updatedRecord);
		this.upsertCacheEntry(updatedRecord);
	}

	async detachServerIdentity(id: string): Promise<void> {
		const record = await this.getExistingOrThrow(id);
		const updatedRecord: LocalInterview = { ...record, interviewId: null, recordingId: null };

		await this.save(updatedRecord);
		this.upsertCacheEntry(updatedRecord);
	}

	async remove(id: string): Promise<void> {
		const database = await this.openDatabase();
		const transaction = database.transaction(LOCAL_INTERVIEWS_STORE_NAME, 'readwrite');
		transaction.objectStore(LOCAL_INTERVIEWS_STORE_NAME).delete(id);
		await waitForIndexedDbTransaction(transaction, TRANSACTION_ERROR_MESSAGE, TRANSACTION_ABORT_MESSAGE);
		this.removeCacheEntry(id);
	}

	private async getExistingOrThrow(id: string): Promise<LocalInterview> {
		const record = await this.get(id);
		if (record === null) {
			throw new Error(INTERVIEW_NOT_FOUND_MESSAGE);
		}

		return record;
	}

	private async save(record: LocalInterview): Promise<void> {
		const database = await this.openDatabase();
		const transaction = database.transaction(LOCAL_INTERVIEWS_STORE_NAME, 'readwrite');
		transaction
			.objectStore(LOCAL_INTERVIEWS_STORE_NAME)
			.put(IndexedDbLocalInterviewStore.toStoredRecord(record));
		await waitForIndexedDbTransaction(transaction, TRANSACTION_ERROR_MESSAGE, TRANSACTION_ABORT_MESSAGE);
	}

	/** Reads only the started owner's records, through the `byOwner` index, rather than the whole store. */
	private async hydrate(startedOwnerUserId: number): Promise<void> {
		const database = await this.openDatabase();
		const transaction = database.transaction(LOCAL_INTERVIEWS_STORE_NAME, 'readonly');
		const request = transaction
			.objectStore(LOCAL_INTERVIEWS_STORE_NAME)
			.index('byOwner')
			.getAll(startedOwnerUserId);
		const records: readonly StoredLocalInterview[] = await waitForIndexedDbRequest(
			request,
			OPERATION_ERROR_MESSAGE,
		);

		if (!this.isStarted || this.ownerUserId !== startedOwnerUserId) {
			return;
		}

		this.interviewsCache.clear();
		for (const record of records) {
			const localInterview = IndexedDbLocalInterviewStore.toLocalInterview(record);
			this.interviewsCache.set(localInterview.id, localInterview);
		}
		this.hydrated = true;
		this.recomputeSnapshotArray();
		this.notifyListeners();
	}

	private upsertCacheEntry(record: LocalInterview): void {
		if (this.ownerUserId === null || record.ownerUserId !== this.ownerUserId) {
			return;
		}
		this.interviewsCache.set(record.id, record);
		this.recomputeSnapshotArray();
		this.notifyListeners();
	}

	private removeCacheEntry(id: string): void {
		const existed = this.interviewsCache.delete(id);
		if (existed) {
			this.recomputeSnapshotArray();
			this.notifyListeners();
		}
	}

	/**
	 * Always allocates a fresh array, deliberately, even when `interviewsCache` is empty. Reusing
	 * `FROZEN_EMPTY_SNAPSHOT` on an empty cache would leave the notification fired at the end of
	 * `hydrate()` carrying the same reference as before, silently breaking `isHydrated()`'s
	 * observability guarantee on any dossier that has no local interview.
	 */
	private recomputeSnapshotArray(): void {
		this.cachedSnapshotArray = Object.freeze(Array.from(this.interviewsCache.values()));
	}

	private notifyListeners(): void {
		const snapshotToDeliver = this.snapshot();
		for (const listener of this.listeners) {
			listener(snapshotToDeliver);
		}
	}

	private static toStoredRecord(record: LocalInterview): StoredLocalInterview {
		const { subject, ...rest } = record;

		return { ...rest, subjectTypeKey: subject.typeKey, subjectId: subject.subjectId };
	}

	/**
	 * Rebuilds the subject address of a stored row; a row without `subjectTypeKey` predates typed
	 * subjects and is a beneficiary. A record persisted before `participants` became an array is read back
	 * as-is by IndexedDB; anything not already an array is normalized to an empty list.
	 */
	private static toLocalInterview(record: StoredLocalInterview): LocalInterview {
		const { subjectTypeKey, subjectId, beneficiaryId, ...rest } = record;
		const subject =
			subjectTypeKey === undefined
				? SubjectAddress.forBeneficiary(beneficiaryId ?? Number.NaN)
				: new SubjectAddress(subjectTypeKey, subjectId ?? Number.NaN);

		return {
			...rest,
			subject,
			participants: Array.isArray(rest.participants) ? rest.participants : [],
		};
	}

	private openDatabase(): Promise<IDBDatabase> {
		if (this.indexedDbFactory === undefined) {
			return Promise.reject(new Error(INDEXED_DB_UNAVAILABLE_MESSAGE));
		}

		if (this.databasePromise === null) {
			this.databasePromise = openDeviceDatabase(this.indexedDbFactory);
		}

		return this.databasePromise;
	}
}

export default IndexedDbLocalInterviewStore;
